/**
 * Écran de connexion (JWT email + mot de passe). Style éditorial sobre.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { authApi } from '../../services/api';
import { Button } from '../../components/ui/kit';
import { CavoramaLogo } from '../../components/CavoramaLogo';
import { APP_VERSION } from '../../constants/version';

function lastAccount() {
  try { return JSON.parse(localStorage.getItem('vd_last_account') || 'null'); } catch (e) { return null; }
}

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const last = lastAccount();
  const prefillEmail = new URLSearchParams(window.location.search).get('email');
  const [email, setEmail] = useState(prefillEmail || last?.email || '');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState(last?.email || '');
  const [resetSending, setResetSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const u = await login(email, password);
      navigate(u?.is_superadmin ? '/admin' : '/');
    } catch (err) {
      setError(err.message || 'Connexion impossible');
    } finally {
      setLoading(false);
    }
  };

  const sendReset = async (e) => {
    e.preventDefault();
    setResetSending(true);
    try {
      await authApi.requestReset(resetEmail, window.location.origin);
      toast.success('Si un compte existe, un email de réinitialisation vient d\'être envoyé.');
      setResetOpen(false);
    } catch (err) {
      toast.error('Envoi impossible, réessayez.');
    } finally {
      setResetSending(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      {/* Colonne éditoriale */}
      <div className="hidden md:flex flex-col justify-between p-12 hair-r">
        <div>
          <CavoramaLogo className="h-12 md:h-14" testid="brand-link" />
          <h2 className="font-title text-3xl mt-4 text-n700 leading-tight">
            Une carte des vins<br />dont on parle<span className="text-gold">.</span>
          </h2>
          <p className="mt-4 text-n600 max-w-sm">
            Servez vos clients, la cave assistée par IA. La gestion d'une cave d'un coup d'œil :
            scan des étiquettes, entrées et sorties, suivi précis, édition des cartes de service.
          </p>
        </div>
        <div className="text-[12.5px] text-n500 leading-relaxed" data-testid="login-footer">
          <p>Serveur sécurisé et hébergé en France.</p>
          <p>Édité par <a href="https://businesstemple.co" target="_blank" rel="noreferrer" className="vd-link">BusinessTemple.co</a></p>
          <p className="nums mt-1" data-testid="login-app-version">{APP_VERSION}</p>
        </div>
      </div>

      {/* Colonne formulaire */}
      <div className="flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
          {/* Marque (mobile uniquement : la colonne éditoriale est masquée) */}
          <div className="md:hidden mb-6">
            <CavoramaLogo className="h-9" testid="login-brand-mobile" />
            <p className="font-title text-xl text-n700 mt-2">Une carte des vins dont on parle<span className="text-gold">.</span></p>
          </div>
          <p className="kicker">Connexion</p>
          <h2 className="font-title text-4xl mt-1 mb-6">Bienvenue dans votre cave à vin !</h2>

          <label className="block text-[13px] text-n600 mb-1">Email</label>
          <input data-testid="login-email" className="vd-input mb-4" type="email" value={email}
            onChange={(e) => setEmail(e.target.value)} required />

          <label className="block text-[13px] text-n600 mb-1">Mot de passe</label>
          <input data-testid="login-password" className="vd-input mb-2" type="password" value={password}
            onChange={(e) => setPassword(e.target.value)} required />

          <div className="mb-4 text-right">
            <button type="button" className="vd-link text-[12.5px]" onClick={() => { setResetEmail(email); setResetOpen(true); }} data-testid="reset-link">
              Réinitialiser mon mot de passe
            </button>
          </div>

          {error && <p className="text-[13px] text-red-700 mb-3" data-testid="login-error">{error}</p>}

          <Button variant="primary" type="submit" className="w-full h-11" disabled={loading} data-testid="login-submit">
            {loading ? 'Connexion…' : 'Se connecter'}
          </Button>
        </form>
      </div>

      {/* Modale : demande de réinitialisation */}
      {resetOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 animate-fade-in" onClick={() => setResetOpen(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={sendReset}
            className="bg-paper border border-hair rounded-lg shadow-xl p-5 w-full max-w-sm" data-testid="reset-modal">
            <h3 className="font-title text-2xl mb-1">Réinitialiser mon mot de passe</h3>
            <p className="text-[13px] text-n600 mb-4">Saisissez votre email. Si un compte existe, vous recevrez un lien pour choisir un nouveau mot de passe.</p>
            <label className="block text-[13px] text-n600 mb-1">Email</label>
            <input type="email" className="vd-input mb-4" value={resetEmail} onChange={(e) => setResetEmail(e.target.value)} required data-testid="reset-email" />
            <div className="flex justify-end gap-2">
              <Button type="button" onClick={() => setResetOpen(false)}>Annuler</Button>
              <Button variant="primary" type="submit" disabled={resetSending || !resetEmail} data-testid="reset-send">
                {resetSending ? 'Envoi…' : 'Envoyer le lien'}
              </Button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
